import React, { ReactNode } from 'react'
import styles from './Typography.module.scss'
import clsx from 'clsx'

// Tipi di componenti tipografici supportati
export const TYPOGRAPHY_VARIANTS = {
  DISPLAY: 'display',
  HEADLINE: 'headline',
  PARAGRAPH: 'paragraph',
  LABEL: 'label',
  BUTTON: 'button',
  LINK: 'link',
} as const

// Dimensioni supportate per ciascun tipo
export const TYPOGRAPHY_SIZES = {
  [TYPOGRAPHY_VARIANTS.DISPLAY]: ['lg', 'md'],
  [TYPOGRAPHY_VARIANTS.HEADLINE]: ['xl', 'lg', 'md', 'sm', 'xs'],
  [TYPOGRAPHY_VARIANTS.PARAGRAPH]: ['lg', 'md', 'sm', 'xs'],
  [TYPOGRAPHY_VARIANTS.LABEL]: ['lg', 'md', 'xs'],
  [TYPOGRAPHY_VARIANTS.BUTTON]: ['md', 'sm'],
  [TYPOGRAPHY_VARIANTS.LINK]: ['md', 'sm', 'xs'],
} as const

type TypographyVariant =
  (typeof TYPOGRAPHY_VARIANTS)[keyof typeof TYPOGRAPHY_VARIANTS]
type TypographySize<V extends TypographyVariant> =
  (typeof TYPOGRAPHY_SIZES)[V][number]

// Font weight disponibili
export type FontWeight = 'light' | 'regular' | 'medium' | 'semi-bold' | 'bold'

interface TypographyProps<V extends TypographyVariant> {
  variant: V
  size: TypographySize<V>
  weight?: FontWeight
  as?: React.ElementType
  children?: ReactNode
  content?: string
  className?: string
  style?: React.CSSProperties
  dangerouslySetInnerHTML?: boolean
  family?: 'sans' | 'serif'
  align?: 'left' | 'center' | 'right'
  htmlFor?: string
}

/**
 * Componente principale Typography
 * Fornisce stili tipografici consistenti in tutto l'applicazione
 */
const Typography = <V extends TypographyVariant>({
  variant,
  size,
  weight,
  as,
  children,
  content,
  className,
  style,
  dangerouslySetInnerHTML = false,
  family,
  align = 'left',
  htmlFor,
}: TypographyProps<V>) => {
  if (!children && !content) {
    return null
  }

  const getTag = () => {
    if (as) return as

    switch (variant) {
      case TYPOGRAPHY_VARIANTS.HEADLINE:
        return 'h2'
      case TYPOGRAPHY_VARIANTS.PARAGRAPH:
        return 'p'
      case TYPOGRAPHY_VARIANTS.LABEL:
        return 'label'
      case TYPOGRAPHY_VARIANTS.BUTTON:
      case TYPOGRAPHY_VARIANTS.LINK:
      default:
        return 'span'
    }
  }

  const validSizes = (TYPOGRAPHY_SIZES[variant] as unknown as string[]) || []
  const validSize = validSizes.includes(size) ? size : validSizes[0] || 'md'

  const classes = clsx(
    styles[`${variant}-${validSize}`],
    weight && { [styles[`font-weight-${weight}`]]: true },
    family == 'sans' && styles.sans,
    family == 'serif' && styles.serif,
    align && { [styles[`text-${align}`]]: true },
    className
  )

  const Tag = getTag()

  if (dangerouslySetInnerHTML && (children || content)) {
    return (
      <Tag
        className={classes}
        style={style}
        dangerouslySetInnerHTML={{
          __html: children?.toString() || content || '',
        }}
        htmlFor={htmlFor}
      />
    )
  }

  return (
    <Tag
      className={classes}
      style={style}
      htmlFor={htmlFor}
    >
      {children || content}
    </Tag>
  )
}

export default Typography
